import dayjs from 'dayjs'
import { fmtDisplayDate } from '@utils/displayDate'

/**
 * Calendar report — the wire's UTC range, and CB's rows prepared for the grid.
 * Plain module so the page file exports only its component.
 */

const pad = (n) => String(n).padStart(2, '0')
const utcStamp = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`

/** Local midnight of a 'YYYY-MM-DD', or null. */
const localMidnight = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd ?? ''))
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** moment's isDST: this date's offset is ahead of the zone's standard (larger) one. */
const isDst = (d) => {
  const jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset()
  const jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset()
  return d.getTimezoneOffset() < Math.max(jan, jul)
}

/**
 * Spec 203 — the three keys legacy's view adds beside the local dates, computed the
 * way it computes them (CalendarReportHomeView.js:297-300, 341; cbToISOString,
 * CBScripts.js:3576):
 * - `activityDateStartUTC`: local midnight of the first day, in UTC, without a 'Z';
 * - `activityDateEndUTC`: local midnight of the last day in UTC, plus an hour when that
 *   day is in daylight time (cbToISOString's `forRecurrence` branch);
 * - `timedifference`: the viewer's current UTC offset in HOURS. cb2 sent minutes, and
 *   CB applies it with AddHours (AccountingModels.cs:2273-2277), which put every timed
 *   event in the exported file ten days early (measured, spec 203 §3.5).
 * CB casts both UTC dates without checking them (AccountingHelper.BillingEntries.cs:572),
 * so without them the run and the export both failed.
 */
export function calendarUtcWire(start, end, now = new Date()) {
  const s = localMidnight(start)
  const e = localMidnight(end)
  let endUtc = null
  if (e) endUtc = utcStamp(isDst(e) ? new Date(e.getTime() + 3600000) : e)
  return {
    activityDateStartUTC: s ? utcStamp(s) : null,
    activityDateEndUTC: endUtc,
    timedifference: -now.getTimezoneOffset() / 60,
  }
}

/**
 * The calendar day an event belongs to, as 'YYYY-MM-DD'. A timed event's start is a
 * UTC instant (cb-api's ISO '…Z'), shown on the viewer's local day, as legacy's view
 * model does with the viewer's offset. An all-day event's date is CB's own calendar
 * date — its ISO carries the CB server's offset, and the wall date is its first ten
 * characters — so it never slides to a neighbouring day for a viewer in another zone.
 */
export function calendarDay(row) {
  const v = row?.appointmentStartDate ?? row?.appointmentDate
  if (!v) return ''
  if (row?.allDayEvent) return String(v).slice(0, 10)
  const d = dayjs(v)
  return d.isValid() ? d.format('YYYY-MM-DD') : ''
}

// Minutes past local midnight. A recurring event's start/end carry the SERIES' first
// date, so only their time of day orders events within one day.
const timeOfDay = (v) => {
  const d = v ? dayjs(v) : null
  return d && d.isValid() ? d.hour() * 60 + d.minute() : Number.POSITIVE_INFINITY
}

/**
 * CB's rows ready for the grid: each gains `calendarDay` and the Date grouping key
 * `appointmentDateString` (the MVC view model sets that field, the dispatcher sends it
 * null), and they come in legacy's order — day, then start, end and scheduled users
 * (AccountingModels.cs:2257).
 */
export function prepareCalendarRows(rows) {
  if (!Array.isArray(rows)) return rows
  return rows
    .map((r) => {
      const day = calendarDay(r)
      return { ...r, calendarDay: day, appointmentDateString: day ? fmtDisplayDate(day) : '' }
    })
    .sort((a, b) => (a.calendarDay < b.calendarDay ? -1 : a.calendarDay > b.calendarDay ? 1 : 0)
      || (a.allDayEvent === b.allDayEvent ? 0 : a.allDayEvent ? -1 : 1)
      || timeOfDay(a.appointmentStartTime) - timeOfDay(b.appointmentStartTime)
      || timeOfDay(a.appointmentEndTime) - timeOfDay(b.appointmentEndTime)
      || String(a.scheduledUsers ?? '').localeCompare(String(b.scheduledUsers ?? '')))
}
